import { useCallback } from "react";

import type { LinkedDocument } from "@/domains/pm/types";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import {
  canQuickViewDocumentRow,
  documentQuickViewKind,
  useDrawingViewer,
} from "@/domains/documents/hooks/useDrawingViewer";
import { usePermissions } from "@/hooks/usePermissions";

export interface OpenLinkedDoc {
  /** Whether the filename/chip is an affordance at all. */
  canOpen: (doc: LinkedDocument) => boolean;
  /** The filenames in `docs` that `canOpen` rejects — what `DocChipCollapsed`
   *  needs to list a file without offering a click that does nothing. */
  nonOpenableFilenames: (docs: LinkedDocument[]) => Set<string>;
  /** Opens the document. No-op when `canOpen` is false. */
  open: (doc: LinkedDocument) => void;
}

/**
 * Opening a document attached to a PM issue or a chat message — the one path
 * shared by the issue list, kanban cards, the issue detail panel's document
 * band and every chat thread row (issue comments + the building message
 * board).
 *
 * A `.dwg` goes to the native Drawings viewer, never to the document viewer:
 * the viewer's DWG branch renders the converted child PDF, which this branch
 * removed, so it dead-ends in an error toast. Routing is delegated to
 * `useDrawingViewer.openDrawingViewer` rather than hand-rolled — it resolves
 * the document's own project and carries `?project=`, without which
 * `/buildings/:id/drawings` stops at the shell's "pick a project" gate and
 * never reads the ids.
 */
export function useOpenLinkedDoc(
  buildingId: number,
  openViewer: (doc: ViewerDocumentData) => Promise<void>
): OpenLinkedDoc {
  const { canViewDrawingCompareTool } = usePermissions();
  const { openDrawingViewer } = useDrawingViewer([], undefined, {
    enabled: canViewDrawingCompareTool,
    buildingId: String(buildingId),
  });

  const canOpen = useCallback(
    (doc: LinkedDocument) => canQuickViewDocumentRow(doc, canViewDrawingCompareTool),
    [canViewDrawingCompareTool]
  );

  const nonOpenableFilenames = useCallback(
    (docs: LinkedDocument[]) =>
      new Set(docs.filter((doc) => !canOpen(doc)).map((doc) => doc.filename)),
    [canOpen]
  );

  const open = useCallback(
    (doc: LinkedDocument) => {
      if (documentQuickViewKind(doc, canViewDrawingCompareTool, false) === "drawing") {
        openDrawingViewer([doc.id]);
        return;
      }
      if (!canQuickViewDocumentRow(doc, canViewDrawingCompareTool)) return;
      void openViewer({
        id: doc.id,
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: buildingId,
        content_type: doc.content_type,
        size: doc.size,
        created_at: doc.created_at,
        parent_document_id: doc.parent_document_id,
        version: doc.version,
      });
    },
    [buildingId, canViewDrawingCompareTool, openDrawingViewer, openViewer]
  );

  return { canOpen, nonOpenableFilenames, open };
}
